import { 
  Lead, 
  Opportunity, 
  Task, 
  Company, 
  Customer 
} from '../types';
import { tasksService } from './tasksService';
import { opportunitiesService } from './opportunitiesService';
import { leadsService } from './leadsService';
import { companiesService } from './companiesService';
import { getAILanguage, getLanguageConfig, type SupportedLanguage } from '../utils/aiLanguageHelper';
import { whisperUploadName } from '../utils/mediaRecorderAudio';
import { truncateHeadline, truncateDescription } from '../utils/googleAdsHelpers';

// OpenAI API Configuration
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY || '';
const OPENAI_API_URL = 'https://api.openai.com/v1';

// Model Configuration (Updated October 2025)
const OPENAI_MODELS = {
  // GPT-5 Models (AVAILABLE!)
  GPT5: {
    standard: 'gpt-5',                 // Flagship, 256K context, native multimodal
    mini: 'gpt-5-mini',                // Best value, native audio included
    nano: 'gpt-5-nano',                // Ultra-light, edge deployments
    codex: 'gpt-5-codex',              // Code-specific (Sept 15, 2025)
  },
  
  // Audio Models
  STT: {
    primary: 'whisper-1',              // Still most reliable for pure STT
    large: 'whisper-large-v3',        // If available
    legacy: 'whisper-1'                // Standard whisper
  },
  
  // Text-to-Speech Models  
  TTS: {
    primary: 'tts-1-hd',               // High quality
    standard: 'tts-1',                 // Standard quality
    legacy: 'tts-1'                    // Fallback
  },
  
  // Language Models - GPT-5 is PRIMARY
  LLM: {
    primary: 'gpt-5',                  // PRIMARY MODEL - Best performance (GPT-5 Available!)
    secondary: 'gpt-4o',               // Fallback - Still excellent
    premium: 'gpt-4-turbo-preview',   // Fallback if 5/4o not available
    fallback: 'gpt-4',                 // Standard GPT-4
    budget: 'gpt-3.5-turbo',           // Cost-effective option
  }
};

// Function definitions for OpenAI function calling
const CRM_FUNCTIONS = [
  {
    name: 'create_task',
    description: 'Create a new task in the CRM',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Task title' },
        description: { type: 'string', description: 'Task description' },
        dueDate: { type: 'string', description: 'Due date in ISO format' },
        priority: { type: 'string', enum: ['high', 'medium', 'low'] },
        assignedTo: { type: 'string', description: 'User ID to assign task to' }
      },
      required: ['title', 'description']
    }
  },
  {
    name: 'search_opportunities',
    description: 'Search for opportunities by criteria',
    parameters: {
      type: 'object',
      properties: {
        stage: { type: 'string', description: 'Opportunity stage' },
        minValue: { type: 'number', description: 'Minimum opportunity value' },
        maxValue: { type: 'number', description: 'Maximum opportunity value' },
        companyName: { type: 'string', description: 'Company name to filter by' }
      }
    }
  },
  {
    name: 'get_customer_info',
    description: 'Get detailed information about a customer',
    parameters: {
      type: 'object',
      properties: {
        customerName: { type: 'string', description: 'Customer name or company name' }
      },
      required: ['customerName']
    }
  },
  {
    name: 'update_opportunity_stage',
    description: 'Update the stage of an opportunity',
    parameters: {
      type: 'object',
      properties: {
        opportunityId: { type: 'string', description: 'Opportunity ID' },
        newStage: { type: 'string', enum: ['qualification', 'proposal', 'negotiation', 'closed-won', 'closed-lost'] }
      },
      required: ['opportunityId', 'newStage']
    }
  },
  {
    name: 'list_tasks',
    description: 'List tasks based on filters',
    parameters: {
      type: 'object',
      properties: {
        status: { type: 'string', enum: ['pending', 'in_progress', 'completed'] },
        assignedTo: { type: 'string', description: 'Filter by assigned user' },
        dueToday: { type: 'boolean', description: 'Show only tasks due today' }
      }
    }
  },
  {
    name: 'get_sales_summary',
    description: 'Get sales performance summary',
    parameters: {
      type: 'object',
      properties: {
        period: { type: 'string', enum: ['today', 'week', 'month', 'quarter', 'year'] }
      }
    }
  },
  {
    name: 'search_contacts',
    description: 'Search and filter contacts (leads) by various criteria',
    parameters: {
      type: 'object',
      properties: {
        stage: { type: 'string', description: 'Contact stage (untouched, contacted, interested, demo, offer, closing, won, lost)' },
        minScore: { type: 'number', description: 'Minimum lead score (0-100)' },
        maxScore: { type: 'number', description: 'Maximum lead score (0-100)' },
        company: { type: 'string', description: 'Company name to filter by' },
        source: { type: 'string', enum: ['website', 'referral', 'cold-outreach', 'social'], description: 'Lead source' },
        assignedTo: { type: 'string', description: 'Filter by assigned user ID' }
      }
    }
  },
  {
    name: 'get_contact_details',
    description: 'Get detailed information about a specific contact by ID',
    parameters: {
      type: 'object',
      properties: {
        contactId: { type: 'string', description: 'Contact/Lead ID' }
      },
      required: ['contactId']
    }
  },
  {
    name: 'search_companies',
    description: 'Search companies by name, industry, or customer status',
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Company name to search for' },
        industry: { type: 'string', description: 'Industry to filter by' },
        isCustomer: { type: 'boolean', description: 'Filter by customer status' },
        domain: { type: 'string', description: 'Company domain/website' }
      }
    }
  },
  {
    name: 'get_company_details',
    description: 'Get detailed information about a specific company by ID',
    parameters: {
      type: 'object',
      properties: {
        companyId: { type: 'string', description: 'Company ID' }
      },
      required: ['companyId']
    }
  },
  {
    name: 'search_tasks',
    description: 'Search and filter tasks with various criteria including overdue tasks',
    parameters: {
      type: 'object',
      properties: {
        status: { type: 'string', enum: ['pending', 'in_progress', 'completed'], description: 'Task status' },
        priority: { type: 'string', enum: ['high', 'medium', 'low'], description: 'Task priority' },
        assignedTo: { type: 'string', description: 'Filter by assigned user' },
        dueToday: { type: 'boolean', description: 'Show only tasks due today' },
        overdue: { type: 'boolean', description: 'Show only overdue tasks' },
        type: { type: 'string', description: 'Task type (call, email, meeting, follow_up, etc.)' }
      }
    }
  },
  {
    name: 'get_task_details',
    description: 'Get detailed information about a specific task by ID',
    parameters: {
      type: 'object',
      properties: {
        taskId: { type: 'string', description: 'Task ID' }
      },
      required: ['taskId']
    }
  }
];

export const openaiService = {
  /**
   * Check model availability (GPT-4o and GPT-5)
   */
  async checkModelAvailability(): Promise<{ gpt4o: boolean; gpt5: boolean }> {
    try {
      const response = await fetch(`${OPENAI_API_URL}/models`, {
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const models = data.data || [];
        const modelIds = models.map((m: any) => m.id);
        
        // Log available GPT-4o models
        const gpt4oAvailable = modelIds.includes('gpt-4o');
        const gpt5Available = models.some((m: any) => m.id.startsWith('gpt-5'));
        
        if (gpt4oAvailable) {
          console.log('✅ GPT-4o is AVAILABLE - Using as primary model');
        }
        
        return { 
          gpt4o: gpt4oAvailable,
          gpt5: gpt5Available 
        };
      }
      return { gpt4o: false, gpt5: false };
    } catch (error) {
      console.log('Model availability check failed');
      return { gpt4o: false, gpt5: false };
    }
  },

  // Keep legacy function for compatibility
  async checkGPT5Available(): Promise<boolean> {
    const availability = await this.checkModelAvailability();
    return availability.gpt5;
  },

  /**
   * General AI query without CRM context - for company enrichment, etc.
   */
  async generalQuery(
    query: string,
    systemPrompt?: string
  ): Promise<string> {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }

    const finalSystemPrompt = systemPrompt || 'You are a helpful AI assistant.';
    
    // Try different models with fallback (GPT-5-mini primary, released August 2025)
    const models = ['gpt-5-mini', 'gpt-5', 'gpt-4o', 'gpt-4-turbo-preview', 'gpt-3.5-turbo'];
    let lastError: Error | null = null;
    
    for (const model of models) {
      try {
        const response = await fetch(`${OPENAI_API_URL}/chat/completions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${OPENAI_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: model,
            messages: [
              { role: 'system', content: finalSystemPrompt },
              { role: 'user', content: query }
            ],
            temperature: 1, // Default temperature for compatibility
            max_completion_tokens: 1000
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: { message: response.statusText } }));
          console.warn(`Model ${model} failed:`, errorData.error?.message || response.statusText);
          lastError = new Error(errorData.error?.message || `OpenAI API error: ${response.statusText}`);
          continue;
        }

        const data = await response.json();
        console.log(`✅ General query using model: ${model}`);
        return data.choices[0].message.content || '';
      } catch (error: any) {
        console.warn(`Model ${model} failed:`, error.message);
        lastError = error;
        continue;
      }
    }
    
    throw lastError || new Error('All AI models failed');
  },

  /**
   * Process audio directly with GPT-5 (native multimodal)
   */
  async processAudioWithGPT5(audioBlob: Blob, context: any): Promise<{ text: string; response: string }> {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }

    try {
      // Convert blob to base64 for GPT-5 multimodal input
      const arrayBuffer = await audioBlob.arrayBuffer();
      const base64Audio = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

      const response = await fetch(`${OPENAI_API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-5-mini', // Use GPT-5-mini (released August 2025)
          messages: [
            {
              role: 'system',
              content: 'You are Aideline, a helpful CRM assistant. Process the user input and respond appropriately.'
            },
            {
              role: 'user',
              content: 'Process this voice command for the CRM system.'
            }
          ],
          max_completion_tokens: 500,
          temperature: 1, // Default temperature for compatibility
          functions: CRM_FUNCTIONS,
          function_call: 'auto'
        })
      });

      if (!response.ok) {
        // Fallback to traditional pipeline if GPT-5 audio fails
        throw new Error('GPT-5 audio processing not available');
      }

      const data = await response.json();
      const message = data.choices[0].message;
      
      return {
        text: 'Voice command processed',
        response: message.content || 'Command processed successfully'
      };
    } catch (error) {
      console.log('GPT-5 native audio failed, using traditional pipeline');
      throw error;
    }
  },
  
  /**
   * Convert speech to text using OpenAI STT API
   */
  async speechToText(audioBlob: Blob, useHighAccuracy: boolean = true): Promise<string> {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }

    const formData = new FormData();
    formData.append('file', audioBlob, whisperUploadName(audioBlob, 'audio'));
    
    // Use Whisper for speech-to-text (GPT-5 native audio is handled differently)
    let model = OPENAI_MODELS.STT.primary; // whisper-1
    
    // Try new model first, fallback to legacy if not available
    const tryTranscribe = async (modelName: string): Promise<any> => {
      formData.set('model', modelName);
      formData.set('language', 'en'); // Or detect language
      
      const response = await fetch(`${OPENAI_API_URL}/audio/transcriptions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 404 && modelName !== OPENAI_MODELS.STT.legacy) {
          // Model not found, try legacy
          console.warn(`Model ${modelName} not available, falling back to whisper-1`);
          return tryTranscribe(OPENAI_MODELS.STT.legacy);
        }
        throw new Error(`STT API error: ${response.statusText}`);
      }

      return response.json();
    };

    try {
      const data = await tryTranscribe(model);
      return data.text;
    } catch (error) {
      console.error('Speech to text error:', error);
      throw error;
    }
  },

  /**
   * Convert text to speech using TTS API
   */
  async textToSpeech(
    text: string, 
    voice: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer' = 'nova',
    options: {
      quality?: 'standard' | 'premium';
      speed?: number;
      emotion?: string;
    } = {}
  ): Promise<ArrayBuffer> {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }

    // Select model based on quality preference
    const model = options.quality === 'premium' 
      ? OPENAI_MODELS.TTS.primary  // Use tts-1-hd for premium
      : OPENAI_MODELS.TTS.standard; // Use tts-1 for standard

    const tryTTS = async (modelName: string): Promise<Response> => {
      const body: any = {
        model: modelName,
        input: text,
        voice: voice,
        speed: options.speed || 1.0
      };

      // Add new 2025 parameters if model supports them
      if (modelName.includes('gpt-4o') && options.emotion) {
        body.emotion = options.emotion; // New in March 2025
        body.style = 'conversational';   // New in March 2025
      }

      const response = await fetch(`${OPENAI_API_URL}/audio/speech`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok && response.status === 404 && modelName !== OPENAI_MODELS.TTS.legacy) {
        console.warn(`Model ${modelName} not available, falling back to tts-1`);
        return tryTTS(OPENAI_MODELS.TTS.legacy);
      }

      return response;
    };

    try {
      const response = await tryTTS(model);
      
      if (!response.ok) {
        throw new Error(`TTS API error: ${response.statusText}`);
      }

      return await response.arrayBuffer();
    } catch (error) {
      console.error('Text to speech error:', error);
      throw error;
    }
  },

  /**
   * Process natural language query with GPT-4 and function calling
   */
  async processQuery(
    query: string, 
    context: {
      userId: string;
      orgId: string;
      userName?: string;
      role?: 'admin' | 'manager' | 'sales_rep' | string;
    }
  ): Promise<{
    response: string;
    data?: any;
    action?: string;
  }> {
    if (!OPENAI_API_KEY) {
      throw new Error('OpenAI API key not configured');
    }

    const systemPrompt = `You are Aideline, an AI assistant for a CRM system. You help users manage tasks, opportunities, companies, and customers. 
    Current user: ${context.userName || 'User'} (Role: ${context.role || 'user'})
    Organization ID: ${context.orgId}
    
    You can perform the following actions:
    - Create and manage tasks
    - Search and update opportunities
    - Get customer information
    - Provide sales summaries and insights
    
    Be concise, helpful, and professional. When creating tasks or updating data, confirm the action taken.`;

    // Try different models with fallback - GPT-4o is PRIORITIZED
    const models = ['gpt-5-mini', 'gpt-5', 'gpt-4o', 'gpt-4-turbo-preview', 'gpt-3.5-turbo'];
    let lastError: Error | null = null;
    
    for (const model of models) {
      try {
        const isGPT5 = model.startsWith('gpt-5');
        const isGPT4o = model === 'gpt-4o';
        
        const response = await fetch(`${OPENAI_API_URL}/chat/completions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${OPENAI_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: model,
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: query }
            ],
            functions: CRM_FUNCTIONS,
            function_call: 'auto',
            temperature: 1, // Default temperature for compatibility
            // Use correct parameter based on model  
            // Note: GPT-4o still uses max_tokens, not max_completion_tokens
            ...(isGPT5 ? { max_completion_tokens: 500 } : { max_tokens: 500 })
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: { message: response.statusText } }));
          console.warn(`Model ${model} failed:`, errorData.error?.message || response.statusText);
          lastError = new Error(errorData.error?.message || `OpenAI API error: ${response.statusText}`);
          continue; // Try next model
        }

        // Success - process the response
        console.log(`✅ Successfully using model: ${model}${model === 'gpt-5-mini' ? ' (PRIMARY - GPT-5 MINI)' : model === 'gpt-5' ? ' (GPT-5 STANDARD)' : ''}`);
        const data = await response.json();
        const message = data.choices[0].message;

        // Check if the model wants to call a function
        if (message.function_call) {
          const functionName = message.function_call.name;
          const functionArgs = JSON.parse(message.function_call.arguments);
          
          // Execute the function and get results
          const functionResult = await this.executeCRMFunction(
            functionName, 
            functionArgs, 
            context
          );

          // Get a natural language response about the function result
          const followUpResponse = await this.getFunctionResultResponse(
            query,
            functionName,
            functionArgs,
            functionResult,
            context
          );

          return {
            response: followUpResponse,
            data: functionResult,
            action: functionName
          };
        }

        // Regular response without function calling
        return {
          response: message.content || 'I understand your request. How can I help you further?'
        };
      } catch (error) {
        console.warn(`Model ${model} threw error:`, error);
        lastError = error as Error;
        continue; // Try next model
      }
    }
    
    // All models failed
    console.error('All models failed. Last error:', lastError);
    throw lastError || new Error('All OpenAI models failed. Please check your API key and try again.');
  },

  /**
   * Execute CRM functions based on OpenAI function calls
   */
  async executeCRMFunction(
    functionName: string, 
    args: any, 
    context: { userId: string; orgId: string; role?: 'admin' | 'manager' | 'sales_rep' | string }
  ): Promise<any> {
    // Role-based access: sales_rep sees only their data, managers/admins see org-wide
    const isSalesRep = context.role === 'sales_rep';
    const userIdFilter = isSalesRep ? context.userId : (args.assignedTo || undefined);
    switch (functionName) {
      case 'create_task':
        return await tasksService.createTask({
          ...args,
          assignedTo: args.assignedTo || context.userId,
          createdBy: context.userId,
          orgId: context.orgId,
          visibility: 'public',
          status: 'pending'
        });

      case 'search_opportunities':
        const opportunities = await opportunitiesService.getOpportunities(
          context.userId,
          context.orgId
        );
        
        // Filter based on args
        return opportunities.filter(opp => {
          if (args.stage && opp.stage !== args.stage) return false;
          if (args.minValue && opp.value < args.minValue) return false;
          if (args.maxValue && opp.value > args.maxValue) return false;
          if (args.companyName && !opp.companyName?.toLowerCase().includes(args.companyName.toLowerCase())) return false;
          return true;
        });

      case 'get_customer_info':
        const companies = await companiesService.getCompanies(context.orgId);
        const customerCompanies = companies.filter(c => c.isCustomer);
        return customerCompanies.find(c => 
          c.name?.toLowerCase().includes(args.customerName.toLowerCase())
        );

      case 'update_opportunity_stage':
        await opportunitiesService.updateOpportunity(args.opportunityId, {
          stage: args.newStage
        });
        return { success: true, opportunityId: args.opportunityId, newStage: args.newStage };

      case 'list_tasks':
        const tasks = await tasksService.getTasks(context.userId, context.orgId);
        return tasks.filter(task => {
          if (args.status && task.status !== args.status) return false;
          if (args.assignedTo && task.assignedTo !== args.assignedTo) return false;
          if (args.dueToday) {
            const today = new Date().toDateString();
            const taskDue = new Date(task.dueDate).toDateString();
            if (today !== taskDue) return false;
          }
          return true;
        });

      case 'get_sales_summary':
        // This would normally aggregate data based on the period
        const allOpportunities = await opportunitiesService.getOpportunities(
          context.userId,
          context.orgId
        );
        
        const wonOpportunities = allOpportunities.filter(o => o.stage === 'closed-won');
        const totalValue = wonOpportunities.reduce((sum, o) => sum + (o.value || 0), 0);
        const pipelineValue = allOpportunities
          .filter(o => !['closed-won', 'closed-lost'].includes(o.stage))
          .reduce((sum, o) => sum + (o.value || 0), 0);
        
        return {
          period: args.period,
          wonDeals: wonOpportunities.length,
          totalRevenue: totalValue,
          pipelineValue: pipelineValue,
          totalOpportunities: allOpportunities.length
        };

      case 'search_contacts':
        const allContacts = await leadsService.getLeads(context.userId, context.orgId);
        return allContacts.filter(contact => {
          // Role-based filtering: sales_rep only sees their own contacts
          if (isSalesRep && contact.assignedTo !== context.userId) return false;
          
          if (args.stage && contact.stage !== args.stage) return false;
          if (args.minScore && contact.score < args.minScore) return false;
          if (args.maxScore && contact.score > args.maxScore) return false;
          if (args.company && !contact.company?.toLowerCase().includes(args.company.toLowerCase())) return false;
          if (args.source && contact.source !== args.source) return false;
          if (args.assignedTo && contact.assignedTo !== args.assignedTo) return false;
          return true;
        });

      case 'get_contact_details':
        return await leadsService.getLeadById(args.contactId);

      case 'search_companies':
        const allCompanies = await companiesService.getCompanies(context.orgId);
        return allCompanies.filter(company => {
          if (args.name && !company.name?.toLowerCase().includes(args.name.toLowerCase())) return false;
          if (args.industry && !company.industry?.toLowerCase().includes(args.industry.toLowerCase())) return false;
          if (args.isCustomer !== undefined && company.isCustomer !== args.isCustomer) return false;
          if (args.domain && !company.domain?.toLowerCase().includes(args.domain.toLowerCase())) return false;
          return true;
        });

      case 'get_company_details':
        const companyList = await companiesService.getCompanies(context.orgId);
        return companyList.find(c => c.id === args.companyId);

      case 'search_tasks':
        const allTasks = await tasksService.getTasks(context.userId, context.orgId);
        const now = new Date();
        
        return allTasks.filter(task => {
          // Role-based filtering: sales_rep only sees their own tasks
          if (isSalesRep && task.assignedTo !== context.userId) return false;
          
          if (args.status && task.status !== args.status) return false;
          if (args.priority && task.priority !== args.priority) return false;
          if (args.assignedTo && task.assignedTo !== args.assignedTo) return false;
          if (args.type && task.type !== args.type) return false;
          
          if (args.dueToday) {
            const today = now.toDateString();
            const taskDue = new Date(task.dueDate).toDateString();
            if (today !== taskDue) return false;
          }
          
          if (args.overdue) {
            const taskDueDate = new Date(task.dueDate);
            if (taskDueDate >= now || task.status === 'completed') return false;
          }
          
          return true;
        });

      case 'get_task_details':
        const tasksList = await tasksService.getTasks(context.userId, context.orgId);
        return tasksList.find(t => t.id === args.taskId);

      default:
        throw new Error(`Unknown function: ${functionName}`);
    }
  },

  /**
   * Generate natural language response for function results
   */
  async getFunctionResultResponse(
    originalQuery: string,
    functionName: string,
    functionArgs: any,
    functionResult: any,
    context: any
  ): Promise<string> {
    if (!OPENAI_API_KEY) {
      return this.getDefaultFunctionResponse(functionName, functionResult);
    }

    // Try models with fallback for response generation - GPT-4o PRIORITIZED
    const models = ['gpt-5-mini', 'gpt-5', 'gpt-4o', 'gpt-4-turbo-preview', 'gpt-3.5-turbo'];
    
    for (const model of models) {
      try {
        const isGPT5 = model.startsWith('gpt-5');
        const isGPT4o = model === 'gpt-4o';
        const response = await fetch(`${OPENAI_API_URL}/chat/completions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${OPENAI_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: model,
            messages: [
              {
                role: 'system',
                content: 'You are Aideline, a helpful CRM assistant. Provide a concise, friendly response about the action that was just performed.'
              },
              {
                role: 'user',
                content: `User asked: "${originalQuery}"
                
                Function called: ${functionName}
                Function arguments: ${JSON.stringify(functionArgs)}
                Result: ${JSON.stringify(functionResult)}
                
                Provide a natural, concise response confirming the action or presenting the information.`
              }
            ],
            temperature: 1, // Default temperature for compatibility
            // GPT-4o still uses max_tokens, not max_completion_tokens
            max_completion_tokens: 150
          })
        });

        if (response.ok) {
          console.log(`✅ Response generation using: ${model}${model === 'gpt-5-mini' ? ' (PRIMARY - GPT-5 MINI)' : ''}`);
          const data = await response.json();
          return data.choices[0].message.content || this.getDefaultFunctionResponse(functionName, functionResult);
        }
        
        // Model failed, try next one
        console.warn(`Model ${model} failed for response generation`);
      } catch (error) {
        console.warn(`Model ${model} error in response generation:`, error);
        continue; // Try next model
      }
    }

    // All models failed, use default response
    console.error('All models failed for response generation, using default');
    return this.getDefaultFunctionResponse(functionName, functionResult);
  },

  /**
   * Default responses when OpenAI is not available
   */
  getDefaultFunctionResponse(functionName: string, result: any): string {
    switch (functionName) {
      case 'create_task':
        return `✅ Task created successfully! I've added "${result.title}" to your task list.`;
      
      case 'search_opportunities':
        if (result.length === 0) {
          return 'No opportunities found matching your criteria.';
        }
        return `Found ${result.length} opportunit${result.length === 1 ? 'y' : 'ies'} matching your search.`;
      
      case 'get_customer_info':
        if (!result) {
          return 'Customer not found. Please check the name and try again.';
        }
        return `Here's the information for ${result.name}.`;
      
      case 'update_opportunity_stage':
        return `✅ Opportunity updated! Stage changed to ${result.newStage}.`;
      
      case 'list_tasks':
        if (result.length === 0) {
          return 'No tasks found matching your criteria.';
        }
        return `Found ${result.length} task${result.length === 1 ? '' : 's'}.`;
      
      case 'get_sales_summary':
        return `Sales Summary (${result.period}): ${result.wonDeals} won deals, ${result.totalRevenue.toLocaleString()} € Umsatz, ${result.pipelineValue.toLocaleString()} € in Pipeline.`;
      
      default:
        return 'Action completed successfully!';
    }
  },

  /**
   * Stream response using Server-Sent Events (for future enhancement)
   */
  async *streamResponse(
    query: string,
    context: any
  ): AsyncGenerator<string> {
    if (!OPENAI_API_KEY) {
      yield 'OpenAI API key not configured. Please add your API key to use voice features.';
      return;
    }

    try {
      const response = await fetch(`${OPENAI_API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: OPENAI_MODELS.LLM.budget, // Use budget model for streaming by default
          messages: [
            { role: 'system', content: 'You are Aideline, a helpful CRM assistant.' },
            { role: 'user', content: query }
          ],
          stream: true,
          temperature: 1 // gpt-5-mini only supports temperature: 1 (default)
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ') && line !== 'data: [DONE]') {
              try {
                const data = JSON.parse(line.slice(6));
                const content = data.choices[0]?.delta?.content;
                if (content) {
                  yield content;
                }
              } catch (e) {
                // Skip invalid JSON
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      yield 'Sorry, I encountered an error. Please try again.';
    }
  },

  /**
   * Generate text with OpenAI (for offer builder, descriptions, etc.)
   */
  async generateText(prompt: string, maxTokens: number = 500): Promise<string> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    const models = ['gpt-5-mini', 'gpt-5', 'gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo'];
    let lastError: Error | null = null;

    for (const model of models) {
      try {
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            model,
            messages: [
              {
                role: 'system',
                content: 'You are a professional business writer. Generate clear, persuasive, and professional content.'
              },
              {
                role: 'user',
                content: prompt
              }
            ],
            max_completion_tokens: maxTokens, // Updated for newer models
            temperature: 1 // gpt-5-mini only supports temperature: 1 (default)
          })
        });

        if (!response.ok) {
          throw new Error(`OpenAI API error: ${response.statusText}`);
        }

        const data = await response.json();
        return data.choices[0].message.content.trim();
      } catch (error) {
        console.warn(`Model ${model} failed:`, error);
        lastError = error as Error;
        continue;
      }
    }

    throw lastError || new Error('All OpenAI models failed');
  },

  /**
   * Generate image using DALL-E
   */
  async generateImage(
    prompt: string, 
    size: '1024x1024' | '1792x1024' | '1024x1792' = '1024x1024'
  ): Promise<string> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    try {
      console.log('Generating image with DALL-E 3...');
      const response = await fetch('https://api.openai.com/v1/images/generations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'dall-e-3',
          prompt: prompt,
          n: 1,
          size: size,
          quality: 'hd', // Use HD quality for better images
          style: 'vivid' // Vivid style for more dramatic, extravagant images
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(error.error?.message || `DALL-E API error: ${response.statusText}`);
      }

      const data = await response.json();
      const imageUrl = data.data[0].url;
      
      console.log('✅ Image generated successfully with DALL-E 3');
      return imageUrl;
    } catch (error) {
      console.error('Error generating image:', error);
      throw error;
    }
  },

  /**
   * Chat with messages array (for general conversations)
   * Now supports language parameter for AI responses in the correct language
   */
  async chat(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    options: {
      model?: string;
      temperature?: number;
      maxTokens?: number;
      language?: SupportedLanguage; // Optional language for AI responses
    } = {}
  ): Promise<string> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    const model = options.model || 'gpt-5-mini';
    
    // Add language instruction to system message if language is specified
    let messagesWithLanguage = messages;
    if (options.language) {
      const langConfig = getLanguageConfig(options.language);
      const languageInstruction = langConfig.code === 'de'
        ? '\n\nWICHTIG: Antworte IMMER auf Deutsch. Verwende keine englischen Begriffe.'
        : '\n\nIMPORTANT: ALWAYS respond in English.';
      
      // Find or create system message
      const systemMessageIndex = messages.findIndex(m => m.role === 'system');
      if (systemMessageIndex >= 0) {
        messagesWithLanguage = [...messages];
        messagesWithLanguage[systemMessageIndex] = {
          ...messages[systemMessageIndex],
          content: messages[systemMessageIndex].content + languageInstruction
        };
      } else {
        messagesWithLanguage = [
          { role: 'system', content: languageInstruction.trim() },
          ...messages
        ];
      }
    }
    
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          messages: messagesWithLanguage,
          temperature: options.temperature || 1, // gpt-5-mini only supports temperature: 1 (default)
          max_completion_tokens: options.maxTokens || 1000
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
        throw new Error(error.error?.message || `OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices[0].message.content.trim();
    } catch (error) {
      console.error('Chat error:', error);
      throw error;
    }
  },

  /**
   * Extract business information from URL using GPT-4o
   * NO CORS ISSUES - OpenAI reads the page directly!
   * STRICT MODE: Only extract data that is clearly stated, no guessing
   */
  async extractBusinessInfoFromUrl(url: string): Promise<any> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    const systemPrompt = `You are a business information extraction specialist. 
    
Visit the provided URL and find business information ONLY in Impressum, Imprint, Contact, or Footer sections.

CRITICAL RULES:
- Extract ONLY information that is clearly stated on the page
- DO NOT guess or invent any information
- DO NOT use default values or assumptions
- If information is not found, use empty string ""
- Better to return empty fields than wrong data
- Look for: Impressum, Imprint, Contact, Legal Notice, Company Information sections

Extract and return ONLY valid JSON:
{
  "businessName": "Company Name (only if clearly stated)",
  "street": "Street Address (only from impressum/contact)",
  "city": "City (only if explicitly mentioned)",
  "postalCode": "Postal/ZIP Code (only if found)",
  "country": "Country (full name like 'Germany', 'United States' - only if stated)",
  "phone": "Phone number (only if found)",
  "email": "Email address (only if found)",
  "source": "impressum|footer|contact|none",
  "confidence": 85
}

If NO business information is found, return all empty strings and confidence: 0.
Return ONLY the JSON, no other text.`;

    try {
      // Try GPT-5 first, then fallback to GPT-4o
      const models = ['gpt-5', 'gpt-4o', 'gpt-4-turbo'];
      
      for (const model of models) {
        try {
          const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({
              model: model,
              messages: [
                { role: 'system', content: systemPrompt },
                { role: 'user', content: `Extract business information from: ${url}` }
              ],
              temperature: 0.1, // Very low temperature for strict factual extraction - no guessing
              max_completion_tokens: 500,
              response_format: { type: 'json_object' }
            })
          });

          if (!response.ok) {
            console.warn(`${model} failed, trying next model...`);
            continue;
          }

          const data = await response.json();
          const businessInfo = JSON.parse(data.choices[0].message.content);
          
          console.log(`✅ Business info extracted using ${model}`);
          return businessInfo;
        } catch (error) {
          console.warn(`${model} error:`, error);
          continue;
        }
      }
      
      throw new Error('All models failed');
    } catch (error) {
      console.error('Error extracting business info with OpenAI:', error);
      throw error;
    }
  },

  /**
   * Detect language from HTML
   */
  detectLanguage(html: string): { code: string; name: string } {
    // Check HTML lang attribute
    const langAttrMatch = html.match(/<html[^>]+lang=["']([a-z]{2}(-[A-Z]{2})?)["']/i);
    if (langAttrMatch) {
      const langCode = langAttrMatch[1].split('-')[0].toLowerCase();
      return this.getLanguageName(langCode);
    }

    // Check meta content-language
    const metaLangMatch = html.match(/<meta[^>]+content=["']([a-z]{2}(-[A-Z]{2})?)["'][^>]*http-equiv=["']content-language["']/i);
    if (metaLangMatch) {
      const langCode = metaLangMatch[1].split('-')[0].toLowerCase();
      return this.getLanguageName(langCode);
    }

    // Detect from content (common words)
    const germanWords = /\b(und|oder|der|die|das|mit|für|über|aus)\b/gi;
    const frenchWords = /\b(et|ou|le|la|les|avec|pour|sur)\b/gi;
    const spanishWords = /\b(y|o|el|la|los|con|para|sobre)\b/gi;

    const germanMatches = (html.match(germanWords) || []).length;
    const frenchMatches = (html.match(frenchWords) || []).length;
    const spanishMatches = (html.match(spanishWords) || []).length;

    if (germanMatches > 10) return { code: 'de', name: 'German' };
    if (frenchMatches > 10) return { code: 'fr', name: 'French' };
    if (spanishMatches > 10) return { code: 'es', name: 'Spanish' };

    // Default to English
    return { code: 'en', name: 'English' };
  },

  /**
   * Get language full name from ISO code
   */
  getLanguageName(code: string): { code: string; name: string } {
    const languages: Record<string, string> = {
      'en': 'English',
      'de': 'German',
      'fr': 'French',
      'es': 'Spanish',
      'it': 'Italian',
      'pt': 'Portuguese',
      'nl': 'Dutch',
      'pl': 'Polish',
      'ru': 'Russian',
      'ja': 'Japanese',
      'zh': 'Chinese',
      'ko': 'Korean',
      'ar': 'Arabic',
      'hi': 'Hindi'
    };
    return { code, name: languages[code] || 'English' };
  },

  /**
   * Extract tracking tags from HTML
   */
  extractTrackingTags(html: string): {
    googleTagManager?: string;
    googleAnalytics?: string;
    googleAds?: string;
    facebookPixel?: string;
    hasConversionTracking: boolean;
    detectedEvents: string[];
  } {
    const tags = {
      googleTagManager: undefined as string | undefined,
      googleAnalytics: undefined as string | undefined,
      googleAds: undefined as string | undefined,
      facebookPixel: undefined as string | undefined,
      hasConversionTracking: false,
      detectedEvents: [] as string[]
    };

    // Extract Google Tag Manager ID
    const gtmMatch = html.match(/GTM-[A-Z0-9]+/);
    if (gtmMatch) {
      tags.googleTagManager = gtmMatch[0];
      tags.hasConversionTracking = true;
    }

    // Extract Google Analytics ID (GA4: G-XXXXXX, Universal: UA-XXXXXX)
    const ga4Match = html.match(/G-[A-Z0-9]+/);
    const uaMatch = html.match(/UA-[0-9]+-[0-9]+/);
    if (ga4Match) {
      tags.googleAnalytics = ga4Match[0];
    } else if (uaMatch) {
      tags.googleAnalytics = uaMatch[0];
    }

    // Extract Google Ads Conversion ID (AW-XXXXXX)
    const awMatch = html.match(/AW-[0-9]+/);
    if (awMatch) {
      tags.googleAds = awMatch[0];
      tags.hasConversionTracking = true;
    }

    // Extract Facebook Pixel ID
    const fbPixelMatch = html.match(/fbq\s*\(\s*['"]init['"],\s*['"](\d+)['"]/);
    if (fbPixelMatch) {
      tags.facebookPixel = fbPixelMatch[1];
      tags.hasConversionTracking = true;
    }

    // Detect common conversion events in scripts
    const eventPatterns = [
      { pattern: /purchase|transaction|buy|checkout/i, event: 'purchase' },
      { pattern: /add_to_cart|addToCart/i, event: 'add_to_cart' },
      { pattern: /sign_up|signup|register/i, event: 'sign_up' },
      { pattern: /lead|contact|submit/i, event: 'lead' },
      { pattern: /download/i, event: 'download' }
    ];

    eventPatterns.forEach(({ pattern, event }) => {
      if (pattern.test(html) && !tags.detectedEvents.includes(event)) {
        tags.detectedEvents.push(event);
      }
    });

    return tags;
  },

  /**
   * Detect language from domain TLD
   */
  detectLanguageFromDomain(url: string): { code: string; name: string } | null {
    const DOMAIN_LANGUAGE_MAP: Record<string, { code: string; name: string }> = {
      '.de': { code: 'de', name: 'German' },
      '.fr': { code: 'fr', name: 'French' },
      '.es': { code: 'es', name: 'Spanish' },
      '.it': { code: 'it', name: 'Italian' },
      '.nl': { code: 'nl', name: 'Dutch' },
      '.at': { code: 'de', name: 'German' },
      '.ch': { code: 'de', name: 'German' }, // Multi-lingual, default German
      '.uk': { code: 'en', name: 'English' },
      '.co.uk': { code: 'en', name: 'English' },
      '.com.au': { code: 'en', name: 'English' },
      '.ca': { code: 'en', name: 'English' },
      '.com': { code: 'en', name: 'English' },
      '.org': { code: 'en', name: 'English' },
    };

    try {
      const hostname = new URL(url).hostname;
      
      // Check for exact TLD matches (e.g., .co.uk, .com.au)
      for (const [tld, lang] of Object.entries(DOMAIN_LANGUAGE_MAP)) {
        if (hostname.endsWith(tld)) {
          return lang;
        }
      }
      
      return null;
    } catch {
      return null;
    }
  },

  /**
   * Analyze landing page from URL (with CORS proxy fallback)
   */
  async analyzeLandingPageFromUrl(url: string): Promise<any> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    // Detect language from domain FIRST
    const domainLang = this.detectLanguageFromDomain(url);

    let pageContent = '';
    let rawHtml = '';
    
    try {
      // Try to fetch the page directly first
      const pageResponse = await fetch(url, { 
        mode: 'cors',
        headers: { 'Accept': 'text/html' }
      });
      
      if (pageResponse.ok) {
        const html = await pageResponse.text();
        rawHtml = html; // Store raw HTML for tag detection
        
        // Extract text content from HTML (remove scripts/styles)
        pageContent = html
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
          .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .substring(0, 8000);
      }
    } catch (fetchError) {
      console.warn('Direct fetch failed (CORS), using URL-based analysis:', fetchError);
      // If fetch fails due to CORS, we'll analyze based on URL only
      pageContent = '';
      rawHtml = '';
    }
    
    // Detect language: Prioritize domain, then validate with content
    let detectedLang = { code: 'en', name: 'English' };
    let languageSource = 'default';
    
    if (domainLang) {
      detectedLang = domainLang;
      languageSource = 'domain';
      console.log(`🌐 Language from domain: ${detectedLang.name} (${detectedLang.code})`);
    }
    
    if (rawHtml) {
      const contentLang = this.detectLanguage(rawHtml);
      console.log(`📄 Language from content: ${contentLang.name} (${contentLang.code})`);
      
      // Validate domain vs content
      if (domainLang && contentLang.code !== domainLang.code) {
        console.warn(`⚠️ Language mismatch! Domain: ${domainLang.name}, Content: ${contentLang.name}`);
        // Trust domain more than content
      } else if (!domainLang) {
        detectedLang = contentLang;
        languageSource = 'content';
      }
    }

    // Extract tracking tags from raw HTML if available
    let trackingInfo = '';
    if (rawHtml) {
      const trackingTags = this.extractTrackingTags(rawHtml);
      if (trackingTags.googleTagManager || trackingTags.googleAnalytics || trackingTags.googleAds) {
        trackingInfo = `\n\nDETECTED TRACKING TAGS:\n${JSON.stringify(trackingTags, null, 2)}`;
      }
    }
    
    const languageInfo = domainLang 
      ? `\n\nDOMAIN LANGUAGE: ${domainLang.name} (${domainLang.code}) - DETECTED FROM TLD\nCONTENT LANGUAGE: ${detectedLang.name} (${detectedLang.code})`
      : `\n\nDETECTED LANGUAGE: ${detectedLang.name} (${detectedLang.code})`;

    const systemPrompt = `You are a precise marketing analyst AI. Analyze the landing page and extract ONLY factual information - DO NOT invent or guess any data.

INDUSTRY DETECTION - BE VERY SPECIFIC:
- Look for clear industry indicators in content, images, and URLs
- AUTOMOTIVE: Keywords like "fahrzeug", "auto", "kfz", "gebrauchtwagen", "neuwagen", "autohaus", "vehicle", "car dealer", "test drive"
  - Domain patterns: autohaus, car-dealer, vehicle, kfz, fahrzeug
  - Visual: car images, showroom photos, vehicle listings
- E-COMMERCE: Shopping cart, product listings, "add to cart", checkout
- SAAS: Free trial, demo, subscription, software features
- REAL ESTATE: Property listings, viewings, "immobilien", apartments
- HEALTHCARE: Appointments, medical services, "termin", doctors
- Be SPECIFIC: "Automotive" or "Car Dealer" instead of generic "Professional Services"
- Use URL clues: mahag.de, autohaus-xyz.de, kfz-handel.de → likely Automotive

LANGUAGE DETECTION:
${domainLang ? `- Domain TLD indicates: ${domainLang.name}` : '- No domain language hint'}
- You MUST validate the actual content language
- If domain and content mismatch, note this in your response

CRITICAL - CONVERSION DETECTION RULES:
ONLY report conversions that ACTUALLY exist on the page with real evidence.
- You MUST find real evidence (forms, buttons, tracking codes, checkout pages)
- Include the exact sub-page URL where conversion happens (e.g., "/checkout", "/contact")
- Include CSS selector or element description
- Include evidence explaining WHY you think this is a conversion
- Confidence must be 0-100 (be CONSERVATIVE - if unsure, mark low confidence)
- If confidence < 70%, don't include it
- If you're guessing or assuming, DON'T include it
- Better to report ZERO conversions than fake ones

TRACKING TAGS:
- Extract existing Google Tag Manager, Analytics, Ads tracking
- Only report tags that actually exist in the HTML

Return your analysis in JSON format with the following structure:
{
  "title": "Page title (from actual page content)",
  "valueProposition": "Main value proposition in 1-2 sentences",
  "targetAudience": "Primary target audience description",
  "keyBenefits": ["benefit1", "benefit2", "benefit3"],
  "features": ["feature1", "feature2", "feature3"],
  "callToAction": "Primary CTA text",
  "industry": "Industry category",
  "suggestedKeywords": ["keyword1", "keyword2", "keyword3", "..."],
  "tone": "professional|casual|urgent|friendly",
  "recommendedCampaignType": "search|pmax",
  "recommendedBudget": {"min": 20, "max": 200, "recommended": 50},
  "detectedLanguage": "en",
  "detectedLanguageName": "English",
  "languageSource": "domain|content|default",
  "languageConfidence": 95,
  "languageMismatch": false,
  "detectedConversions": [
    {
      "type": "purchase|lead|signup|contact|download|pageview",
      "name": "Descriptive name",
      "value": 99.99,
      "confidence": 85,
      "pageUrl": "/checkout",
      "fullUrl": "https://example.com/checkout",
      "selector": "#buy-button",
      "evidence": "Found checkout form with price display and submit button",
      "isReal": true
    }
  ],
  "suggestedConversionValue": 99.99,
  "hasEcommerce": true,
  "hasForms": true,
  "estimatedConversionRate": 2.5,
  "trackingTags": {
    "googleTagManager": "GTM-XXXXXX or null",
    "googleAnalytics": "G-XXXXXX or null",
    "googleAdsConversion": "AW-XXXXXX or null",
    "facebookPixel": "123456789 or null",
    "hasConversionTracking": true,
    "detectedEvents": ["purchase", "add_to_cart", "sign_up"]
  }
}

REMEMBER: Be factual and precise. Don't invent data. Low confidence is better than wrong data.`;

    const userPrompt = pageContent 
      ? `URL: ${url}${languageInfo}\n\nPage Content:\n${pageContent}${trackingInfo}\n\nIMPORTANT: Generate keywords and content in ${detectedLang.name} language.`
      : `URL: ${url}\n\nBased on the URL and domain name, create a reasonable marketing analysis for a Google Ads campaign. Use industry best practices and make intelligent assumptions. Language: English.`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-5-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          temperature: 1, // Default temperature (0.1 not supported by some models)
          max_completion_tokens: 2000, // Updated for newer models
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('OpenAI API error details:', errorData);
        throw new Error(`OpenAI API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      const analysis = JSON.parse(data.choices[0].message.content);
      
      // Filter out low-confidence conversions (< 70%)
      if (analysis.detectedConversions && Array.isArray(analysis.detectedConversions)) {
        const originalCount = analysis.detectedConversions.length;
        analysis.detectedConversions = analysis.detectedConversions.filter((conv: any) => {
          const confidence = conv.confidence || 0;
          const hasEvidence = conv.evidence && conv.evidence.length > 10;
          return confidence >= 70 && hasEvidence;
        });
        
        if (originalCount > analysis.detectedConversions.length) {
          console.warn(`⚠️ Filtered out ${originalCount - analysis.detectedConversions.length} low-confidence conversions`);
        }
        
        console.log(`✅ Found ${analysis.detectedConversions.length} reliable conversions`);
      }
      
      // Add language source info
      analysis.languageSource = languageSource;
      analysis.domainLanguage = domainLang;
      
      console.log('✅ Landing page analyzed successfully');
      return analysis;
    } catch (error) {
      console.error('❌ Error analyzing landing page with OpenAI:', error);
      throw new Error(`OpenAI analysis failed: ${(error as Error).message}. Please ensure the website is accessible and try again.`);
    }
  },

  /**
   * Analyze landing page content for Google Ads campaign creation (legacy)
   * @deprecated Use analyzeLandingPageFromUrl instead
   */
  async analyzeLandingPageContent(html: string, url: string): Promise<any> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    // Extract text content from HTML (simple approach)
    const textContent = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 8000); // Limit to 8000 chars

    const systemPrompt = `You are a marketing analyst AI. Analyze the landing page content and extract key marketing intelligence for creating Google Ads campaigns. Return your analysis in JSON format with the following structure:
{
  "title": "Page title",
  "valueProposition": "Main value proposition in 1-2 sentences",
  "targetAudience": "Primary target audience description",
  "keyBenefits": ["benefit1", "benefit2", "benefit3"],
  "features": ["feature1", "feature2", "feature3"],
  "callToAction": "Primary CTA text",
  "industry": "Industry category",
  "suggestedKeywords": ["keyword1", "keyword2", "keyword3", "..."],
  "tone": "professional|casual|urgent|friendly",
  "recommendedCampaignType": "search|pmax",
  "recommendedBudget": {"min": 20, "max": 200, "recommended": 50}
}`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-5-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `URL: ${url}\n\nPage Content:\n${textContent}` }
          ],
          temperature: 1, // Default temperature for compatibility
          max_completion_tokens: 1500, // Updated for newer models
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.statusText}`);
      }

      const data = await response.json();
      const analysis = JSON.parse(data.choices[0].message.content);
      
      console.log('✅ Landing page analyzed successfully');
      return analysis;
    } catch (error) {
      console.error('Error analyzing landing page:', error);
      throw error;
    }
  },

  /**
   * Generate ad copy (headlines and descriptions) based on landing page analysis
   * 
   * @param analysis Landing page analysis data
   * @param campaignType 'search' or 'pmax'
   * @param count Number of headlines/descriptions to generate
   * @param voiceContext Optional VoiceAnalysis for brand-consistent copy
   * @param targetLanguage Override language (user selection takes priority over detected)
   */
  async generateAdCopy(
    analysis: any,
    campaignType: 'search' | 'pmax',
    count: { headlines: number; descriptions: number },
    voiceContext?: {
      addressForm?: 'Sie' | 'Du' | 'mixed';
      formality?: 'formal' | 'informal' | 'neutral';
      toneAttributes?: string[];
      keyMessages?: string[];
      doStatements?: string[];
      dontStatements?: string[];
    },
    targetLanguage?: string // User-selected language overrides detected
  ): Promise<{ headlines: string[]; descriptions: string[]; longHeadlines?: string[] }> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    // Use targetLanguage (user selection) if provided, otherwise fall back to detected
    const detectedLanguage = targetLanguage || analysis.detectedLanguageName || 'English';
    const isGerman = detectedLanguage === 'German' || detectedLanguage.toLowerCase() === 'deutsch';
    
    const languageInstruction = isGerman 
      ? `🇩🇪 CRITICAL: Generate ALL content in GERMAN (Deutsch) language ONLY! 
         NO English words allowed! Use German keywords, German CTAs, German benefits.
         Examples: "Jetzt anfragen", "Kostenlos testen", "Mehr erfahren", "Angebot erhalten"
         DO NOT use: "Request", "Free trial", "Learn more", "Get offer"`
      : `Generate all content in ${detectedLanguage} language.`;

    // Build voice instructions if voiceContext is provided
    let voiceInstructions = '';
    if (voiceContext) {
      const addressFormInstruction = voiceContext.addressForm 
        ? `- Address Form: Use "${voiceContext.addressForm}" consistently (${voiceContext.addressForm === 'Sie' ? 'formal German' : voiceContext.addressForm === 'Du' ? 'informal German' : 'mixed'})`
        : '';
      
      const formalityInstruction = voiceContext.formality
        ? `- Formality Level: ${voiceContext.formality}`
        : '';
      
      const toneInstruction = voiceContext.toneAttributes?.length
        ? `- Tone Attributes: ${voiceContext.toneAttributes.join(', ')}`
        : '';
      
      const keyMessagesInstruction = voiceContext.keyMessages?.length
        ? `\nKEY MESSAGES TO INCORPORATE:\n${voiceContext.keyMessages.map(m => `  - ${m}`).join('\n')}`
        : '';
      
      const doInstruction = voiceContext.doStatements?.length
        ? `\nDO:\n${voiceContext.doStatements.map(s => `  ✓ ${s}`).join('\n')}`
        : '';
      
      const dontInstruction = voiceContext.dontStatements?.length
        ? `\nDON'T:\n${voiceContext.dontStatements.map(s => `  ✗ ${s}`).join('\n')}`
        : '';

      voiceInstructions = `
=== BRAND VOICE REQUIREMENTS ===
${addressFormInstruction}
${formalityInstruction}
${toneInstruction}
${keyMessagesInstruction}
${doInstruction}
${dontInstruction}
================================
`;
    }
    
    // Build structured content from analysis for better AI context
    const structuredContent = `
════════════════════════════════════════════════════════════════
📊 WEBSITE CONTENT (USE THIS AS SOURCE)
════════════════════════════════════════════════════════════════
Business Name: ${analysis.title || 'Unknown Business'}
Industry: ${analysis.industry || 'Services'}
USP: ${analysis.valueProposition || 'Professional services'}
Key Benefits: ${(analysis.keyBenefits || []).slice(0, 5).join(', ') || 'Quality, Trust, Expertise'}
Features/Services: ${(analysis.features || []).slice(0, 5).join(', ') || 'Professional solutions'}
Call-to-Action: ${analysis.callToAction || 'Contact us'}
Target Audience: ${analysis.targetAudience || 'Businesses and individuals'}
════════════════════════════════════════════════════════════════`;
    
    const systemPrompt = campaignType === 'search'
      ? `You are an expert Google Ads copywriter creating ads in ${detectedLanguage}.

${languageInstruction}
${voiceInstructions}

════════════════════════════════════════════════════════════════
⚠️ CRITICAL FORMATTING RULES - MUST FOLLOW:
════════════════════════════════════════════════════════════════
1. Headlines: STRICTLY MAX 30 characters each
2. Descriptions: STRICTLY MAX 90 characters each
3. LANGUAGE: ALL text MUST be in ${detectedLanguage} - NO mixing!

❌ FORBIDDEN - NEVER DO THIS:
- NO underscores (_) in any text
- NO keyword slugs like "ai_solutions_consulting"
- NO technical/URL-style formatting
- NO & symbols - use "und" (German) or "and" (English)
- NO abbreviations that look like code

✅ REQUIRED - ALWAYS DO THIS:
- Write natural, human-readable sentences
- Use proper spacing and grammar
- Create compelling, professional ad copy
- Each headline should be unique and varied
- Use the business content provided below

EXAMPLES OF GOOD HEADLINES (${detectedLanguage}):
${isGerman 
  ? '- "KI-Lösungen für Ihr Business"\n- "Jetzt kostenlos beraten"\n- "Experten seit 2010"\n- "Qualität garantiert"'
  : '- "AI Solutions For Business"\n- "Get Free Consultation"\n- "Experts Since 2010"\n- "Quality Guaranteed"'}

EXAMPLES OF BAD HEADLINES (NEVER USE):
- "ai_solutions_&_consulting" ❌
- "Best_AI_Services_2024" ❌
- "professional_solutions_&..." ❌
════════════════════════════════════════════════════════════════

Return ONLY valid JSON:
{
  "headlines": ["headline1", "headline2", ...],
  "descriptions": ["desc1", "desc2", ...]
}`
      : `You are an expert Google Ads copywriter creating Performance Max ads in ${detectedLanguage}.

${languageInstruction}
${voiceInstructions}

════════════════════════════════════════════════════════════════
⚠️ CRITICAL FORMATTING RULES - MUST FOLLOW:
════════════════════════════════════════════════════════════════
1. Headlines: STRICTLY MAX 30 characters each (need ${count.headlines})
2. Long Headlines: STRICTLY MAX 90 characters each (need 3-5)
3. Descriptions: STRICTLY MAX 90 characters each (need ${count.descriptions})
4. LANGUAGE: ALL text MUST be in ${detectedLanguage} - NO mixing!

❌ FORBIDDEN - NEVER DO THIS:
- NO underscores (_) in any text
- NO keyword slugs like "ai_solutions_consulting"
- NO technical/URL-style formatting
- NO & symbols - use "und" (German) or "and" (English)

✅ REQUIRED - ALWAYS DO THIS:
- Write natural, human-readable sentences
- Use proper spacing and grammar
- Create compelling, professional ad copy
════════════════════════════════════════════════════════════════

Return ONLY valid JSON:
{
  "headlines": ["headline1", "headline2", ...],
  "longHeadlines": ["long1", "long2", ...],
  "descriptions": ["desc1", "desc2", ...]
}`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-5-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            { 
              role: 'user', 
              content: `${structuredContent}

Generate ${count.headlines} headlines and ${count.descriptions} descriptions based on the website content above.

⚠️ FINAL REMINDERS:
1. Language: ${detectedLanguage} ONLY - no mixing!
2. NO underscores, NO slugs, NO technical formatting
3. Headlines: max 30 chars | Descriptions: max 90 chars
4. Write natural, compelling ad copy that humans would write
${isGerman 
  ? '5. Deutsche Wörter: "Jetzt", "Kostenlos", "Beratung", "Qualität", "Experten"\n6. KEINE englischen Wörter!'
  : '5. Use professional marketing language'}`
            }
          ],
          temperature: 1, // gpt-5-mini only supports temperature: 1 (default)
          max_completion_tokens: 1000, // Updated for newer models
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`OpenAI API error: ${response.statusText} - ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      const adCopy = JSON.parse(data.choices[0].message.content);
      
      // CRITICAL: Enforce character limits even if AI didn't follow them
      // Truncate all headlines to max 30 characters
      if (adCopy.headlines) {
        adCopy.headlines = adCopy.headlines.map((h: string) => truncateHeadline(h));
      }
      
      // Truncate all descriptions to max 90 characters
      if (adCopy.descriptions) {
        adCopy.descriptions = adCopy.descriptions.map((d: string) => truncateDescription(d));
      }
      
      // Truncate long headlines (for PMax) to max 90 characters
      if (adCopy.longHeadlines) {
        adCopy.longHeadlines = adCopy.longHeadlines.map((lh: string) => truncateDescription(lh));
      }
      
      console.log('✅ Ad copy generated and truncated to meet character limits');
      return adCopy;
    } catch (error) {
      console.error('Error generating ad copy:', error);
      throw error;
    }
  },

  /**
   * Perform deep research using OpenAI's o4-mini-deep-research model
   * Uses the Responses API (not Chat Completions) with web search capability
   * For competitive intelligence, market analysis, and strategic recommendations
   */
  async deepResearch(prompt: string, options?: { max_tokens?: number; background?: boolean }): Promise<string> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    try {
      console.log('🔍 Starting Deep Research with o4-mini-deep-research + web search...');
      
      // TEMPORARY: Responses API might not be available yet in all regions
      // Try it, but expect it to fail and use fallback
      // Use Responses API (NOT Chat Completions) for deep research models
      const response = await fetch(`${OPENAI_API_URL}/responses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'o4-mini-deep-research', // Deep research model
          input: prompt, // Use 'input' not 'messages' for Responses API
          background: options?.background ?? false, // Run in background for long requests
          tools: [
            { type: 'web_search_preview' } // Required: at least one data source
          ],
          modalities: ['text'] // Response format
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: response.statusText } }));
        console.error('Deep Research API error:', errorData);
        console.warn('⚠️ Deep research model not available (expected), using GPT-4o fallback...');
        
        // Fallback to GPT-4o (this is expected for now)
        return await this.fallbackDeepResearch(prompt, options);
      }

      const data = await response.json();
      
      // Extract content from response
      // Response API returns different structure than Chat Completions
      let content = '';
      
      if (data.output && Array.isArray(data.output)) {
        // Find the final message in the output array
        const messageOutput = data.output.find((item: any) => item.type === 'message');
        if (messageOutput?.content) {
          if (Array.isArray(messageOutput.content)) {
            // Content is an array of text blocks
            content = messageOutput.content
              .filter((block: any) => block.type === 'text')
              .map((block: any) => block.text)
              .join('\n');
          } else if (typeof messageOutput.content === 'string') {
            content = messageOutput.content;
          }
        }
      }
      
      if (!content) {
        throw new Error('No content returned from deep research');
      }
      
      console.log('✅ Deep Research completed successfully with web search');
      return content;
      
    } catch (error) {
      console.error('Deep research error:', error);
      // Fallback to GPT-4o
      console.warn('⚠️ Deep research failed, using GPT-4o fallback...');
      return await this.fallbackDeepResearch(prompt, options);
    }
  },

  /**
   * Fallback deep research using GPT-4o (standard chat completions)
   */
  async fallbackDeepResearch(prompt: string, options?: { max_tokens?: number }): Promise<string> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    try {
      console.log('🔍 Running fallback research with GPT-4o...');
      
      const response = await fetch(`${OPENAI_API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [{
            role: 'system',
            content: 'You are a business research expert. Provide detailed, factual analysis based on the given information. Be thorough and strategic. Use your knowledge cutoff data and reasoning capabilities.'
          }, {
            role: 'user',
            content: prompt
          }],
          max_completion_tokens: options?.max_tokens || 8000,
          temperature: 0.3
        })
      });

      if (!response.ok) {
        throw new Error(`GPT-4o fallback failed: ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content || '';
      
      console.log('✅ Fallback research completed with GPT-4o');
      return content;
      
    } catch (error) {
      console.error('Fallback research error:', error);
      throw new Error(`Research service unavailable: ${(error as Error).message}`);
    }
  },

  /**
   * Generate campaign optimization suggestions
   */
  async generateCampaignOptimizations(campaign: any): Promise<any[]> {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      return [];
    }

    const systemPrompt = `You are a Google Ads optimization expert. Analyze the campaign data and provide actionable optimization suggestions. Return JSON array format:
[
  {
    "type": "keyword|bid|budget|ad_copy|targeting",
    "priority": "high|medium|low",
    "title": "Short title",
    "description": "Detailed description",
    "currentValue": "Current state (if applicable)",
    "suggestedValue": "Suggested change (if applicable)",
    "potentialImpact": "Expected impact (e.g., '+15% CTR')",
    "confidence": 75
  }
]`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-5-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            { 
              role: 'user', 
              content: `Campaign Data:\n${JSON.stringify({
                name: campaign.name,
                type: campaign.type,
                status: campaign.status,
                budget: campaign.dailyBudget,
                metrics: campaign.metrics
              }, null, 2)}\n\nProvide 3-5 optimization suggestions.`
            }
          ],
          temperature: 1, // Default temperature for compatibility
          max_tokens: 1000,
          response_format: { type: 'json_object' }
        })
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const result = JSON.parse(data.choices[0].message.content);
      
      return result.suggestions || [];
    } catch (error) {
      console.error('Error generating optimizations:', error);
      return [];
    }
  }
};
